/**
 * Safe Toast Container
 * Wraps ToastContainer with additional error handling and safe close button
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SafeToastContainer = (props) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Safe dismiss function that prevents removalReason errors
    // We avoid calling toast.dismiss() directly to prevent the removalReason error
    const safeDismissToast = (toastElement, toastId) => {
      try {
        // Mark as being removed to prevent double-processing
        if (toastElement.dataset.removing === "true") return;
        toastElement.dataset.removing = "true";

        // Animate out first
        toastElement.style.transition = "opacity 0.2s ease-out, transform 0.2s ease-out";
        toastElement.style.opacity = "0";
        toastElement.style.transform = "translateX(100%)";

        // Remove after animation - directly remove without calling dismiss
        // This prevents the removalReason error
        setTimeout(() => {
          try {
            // Remove directly from DOM to avoid react-toastify's closeToast function
            if (toastElement.parentNode) {
              toastElement.remove();
            }
            
            // Also try to clean up from react-toastify's internal state if possible
            // But don't call dismiss as it causes the error
            if (toastId && window.reactToastify) {
              try {
                // Only if react-toastify exposes a safe cleanup method
                // Otherwise, just removing from DOM is enough
              } catch (e) {
                // Ignore
              }
            }
          } catch (removeError) {
            // Silently handle - element might already be removed
          }
        }, 200);
      } catch (error) {
        // Silently handle all errors
      }
    };

    // Patch close buttons and toast elements after container mounts
    const patchToasts = () => {
      if (!containerRef.current) return;

      // Patch close buttons
      const closeButtons = containerRef.current.querySelectorAll(
        '.Toastify__close-button, [data-testid="close-button"]'
      );

      closeButtons.forEach((button) => {
        // Skip if already patched
        if (button.dataset.safePatched === "true") return;

        // Mark as patched
        button.dataset.safePatched = "true";

        // Add safe close handler
        const safeCloseHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          try {
            const toastElement = button.closest(".Toastify__toast");
            if (!toastElement) return;

            const toastId =
              toastElement.getAttribute("data-toast-id") ||
              toastElement.getAttribute("id") ||
              null;

            // Disable button to prevent double-clicks
            button.style.pointerEvents = "none";
            button.style.opacity = "0.5";

            safeDismissToast(toastElement, toastId);
          } catch (error) {
            // Silently handle
          }
        };

        button.addEventListener("click", safeCloseHandler, {
          capture: true,
          passive: false,
        });
      });

      // Patch toast elements for closeOnClick
      const toastElements = containerRef.current.querySelectorAll(".Toastify__toast");
      toastElements.forEach((toastElement) => {
        // Skip if already patched
        if (toastElement.dataset.safePatched === "true") return;

        // Mark as patched
        toastElement.dataset.safePatched = "true";

        // Intercept click events on the toast itself (for closeOnClick)
        const safeToastClickHandler = (e) => {
          // Only handle if clicking on the toast body (not close button)
          if (e.target.closest('.Toastify__close-button')) {
            return; // Let close button handler deal with it
          }

          // Check if closeOnClick is enabled
          const container = containerRef.current?.querySelector('.Toastify__toast-container');
          if (container && props.closeOnClick) {
            e.preventDefault();
            e.stopPropagation();

            try {
              const toastId =
                toastElement.getAttribute("data-toast-id") ||
                toastElement.getAttribute("id") ||
                null;

              safeDismissToast(toastElement, toastId);
            } catch (error) {
              // Silently handle
            }
          }
        };

        toastElement.addEventListener("click", safeToastClickHandler, {
          capture: true,
          passive: false,
        });
      });
    };

    // Patch immediately
    patchToasts();

    // Also observe for dynamically added toasts
    const observer = new MutationObserver(() => {
      patchToasts();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [props.closeOnClick]);

  return (
    <div ref={containerRef}>
      <ToastContainer
        {...props}
        // Disable default close button and use our patched version
        closeButton={true}
        // Add custom className for easier targeting
        className="safe-toast-container"
      />
    </div>
  );
};

SafeToastContainer.propTypes = {
  closeOnClick: PropTypes.bool,
  position: PropTypes.string,
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  hideProgressBar: PropTypes.bool,
  newestOnTop: PropTypes.bool,
  rtl: PropTypes.bool,
  pauseOnFocusLoss: PropTypes.bool,
  draggable: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  theme: PropTypes.string,
  enableMultiContainer: PropTypes.bool,
  limit: PropTypes.number,
  closeButton: PropTypes.oneOfType([PropTypes.bool, PropTypes.element]),
};

SafeToastContainer.defaultProps = {
  closeOnClick: true,
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: false,
  newestOnTop: false,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: "light",
  enableMultiContainer: false,
  limit: 5,
  closeButton: true,
};

export default SafeToastContainer;

